import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Edit2, Trash2, Eye, EyeOff, Image, Loader2, X, Upload, Calendar, Mic, Sparkles, StopCircle, Star } from "lucide-react";
import { toast } from "sonner";
import api from "../../utils/api";

const API = `/api`;

const SERVICE_TYPES = [
  "Full Respray",
  "Custom Paint",
  "Panel Repair", 
  "Colour Change",
  "Ceramic Coating",
  "Restoration",
  "Show Build",
  "Other"
];

const TAG_OPTIONS = [
  "JDM", "European", "American", "Classic", "Show Car", "Daily Driver",
  "Custom", "OEM+", "Restoration", "Track Car", "Street", "Widebody"
];

function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    title: "",
    vehicle: "",
    service: "Full Respray",
    description: "",
    photos: [],
    tags: [],
    published: false
  });
  
  // Photo upload state
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  
  // AI Description state
  const [aiNotes, setAiNotes] = useState("");
  const [generatingAI, setGeneratingAI] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await api.get(`${API}/admin/blog`);
      setPosts(res.data.posts || []);
    } catch (err) {
      toast.error("Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: "",
      vehicle: "",
      service: "Full Respray",
      description: "",
      photos: [],
      tags: [],
      published: false
    });
    setEditingPost(null);
    setAiNotes("");
  };

  const openEditor = (post = null) => {
    if (post) {
      setEditingPost(post);
      setForm({
        title: post.title || "",
        vehicle: post.vehicle || "",
        service: post.service || "Full Respray",
        description: post.description || "",
        photos: post.photos || [],
        tags: post.tags || [],
        published: post.published || false
      });
    } else {
      resetForm();
    }
    setAiNotes("");
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    resetForm();
  };

  const handlePhotoUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingPhotos(true);
    const newPhotos = [...form.photos];
    
    for (const file of Array.from(files)) {
      if (newPhotos.length >= 20) {
        toast.error("Maximum 20 photos per post");
        break;
      }
      
      // Convert to base64
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newPhotos.push(base64);
    }
    
    setForm(f => ({ ...f, photos: newPhotos }));
    setUploadingPhotos(false);
  };

  const removePhoto = (index) => {
    setForm(f => ({
      ...f,
      photos: f.photos.filter((_, i) => i !== index)
    }));
  };

  const toggleTag = (tag) => {
    setForm(f => ({
      ...f,
      tags: f.tags.includes(tag)
        ? f.tags.filter(t => t !== tag)
        : [...f.tags, tag]
    }));
  };

  // AI Description Generation
  const generateDescription = async () => {
    if (!aiNotes.trim()) {
      toast.error("Add some notes first - describe what you did on this job");
      return;
    }
    if (!form.vehicle) {
      toast.error("Enter the vehicle name first");
      return;
    }
    
    setGeneratingAI(true);
    try {
      const res = await api.post(`${API}/admin/blog/generate-description`, {
        vehicle: form.vehicle,
        service: form.service,
        tags: form.tags,
        notes: aiNotes
      });
      
      setForm(f => ({ ...f, description: res.data.description }));
      toast.success("Description generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI generation failed");
    } finally {
      setGeneratingAI(false);
    }
  };

  // Voice Recording using Web Speech API
  const startRecording = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error("Voice input not supported in this browser. Try Chrome or Edge.");
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-AU';
    
    recognition.onstart = () => {
      setIsRecording(true);
      toast.info("Listening... Speak now");
    };
    
    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      
      if (finalTranscript) {
        setAiNotes(prev => prev + finalTranscript);
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error !== 'no-speech') {
        toast.error(`Voice error: ${event.error}`);
      }
      setIsRecording(false);
    };
    
    recognition.onend = () => {
      setIsRecording(false);
    };
    
    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
      toast.success("Voice note captured!");
    }
  };

  const savePost = async () => {
    if (!form.title || !form.vehicle) {
      toast.error("Title and Vehicle are required");
      return;
    }
    
    setSaving(true);
    try {
      if (editingPost) {
        await api.put(`${API}/admin/blog/${editingPost.id}`, form);
        toast.success("Post updated!");
      } else {
        await api.post(`${API}/admin/blog`, form);
        toast.success("Post created!");
      }
      await fetchPosts();
      closeEditor();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save post");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (post) => {
    try {
      const res = await api.post(`${API}/admin/blog/${post.id}/publish`);
      toast.success(res.data.published ? "Post published!" : "Post unpublished");
      await fetchPosts();
    } catch (err) {
      toast.error("Failed to toggle publish status");
    }
  };

  const setAsFeatured = async (post) => {
    try {
      const res = await api.post(`${API}/admin/blog/${post.id}/set-featured`);
      toast.success(res.data.message || "Set as featured!");
      await fetchPosts();
    } catch (err) {
      toast.error("Failed to set as featured");
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    
    setDeleting(post.id);
    try {
      await api.delete(`${API}/admin/blog/${post.id}`);
      toast.success("Post deleted");
      await fetchPosts();
    } catch (err) {
      toast.error("Failed to delete post");
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={32} className="animate-spin text-[#3b82f6]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl uppercase tracking-wider" style={{ color: 'var(--dash-text)' }}>
            Blog / Portfolio
          </h2>
          <p className="text-sm font-body mt-1" style={{ color: 'var(--dash-text-muted)' }}>
            Showcase completed work on the public blog page
          </p>
        </div>
        <button
          onClick={() => openEditor()}
          className="flex items-center gap-2 px-4 py-2 bg-[#3b82f6] text-white font-heading text-sm tracking-wider uppercase hover:bg-[#3b82f6]/80 transition-colors cursor-pointer border-none"
          data-testid="new-blog-post-btn"
        >
          <Plus size={16} /> New Post
        </button>
      </div>

      {/* Posts Grid */}
      {posts.length === 0 ? (
        <div className="border-2 border-dashed p-12 text-center" style={{ borderColor: 'var(--dash-border)' }}>
          <Image size={48} className="mx-auto mb-4" style={{ color: 'var(--dash-text-dim)' }} />
          <p className="font-body" style={{ color: 'var(--dash-text-muted)' }}>No blog posts yet</p>
          <p className="text-sm mt-2" style={{ color: 'var(--dash-text-dim)' }}>
            Click &ldquo;New Post&rdquo; to showcase a completed job
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map((post) => (
            <div
              key={post.id}
              className="border overflow-hidden"
              style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}
            >
              {/* Thumbnail */}
              <div className="aspect-video bg-zinc-900 relative overflow-hidden">
                {post.photos?.[0] ? (
                  <img
                    src={post.photos[0]}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image size={32} className="text-zinc-700" />
                  </div>
                )}
                {/* Status Badge */}
                <div className={`absolute top-2 right-2 px-2 py-1 text-[10px] uppercase tracking-wider font-body ${
                  post.published 
                    ? 'bg-[#22c55e]/90 text-white' 
                    : 'bg-zinc-800/90 text-zinc-400'
                }`}>
                  {post.published ? 'Published' : 'Draft'}
                </div>
                {/* Featured Badge */}
                {post.featured && (
                  <div className="absolute top-2 left-2 px-2 py-1 text-[10px] uppercase tracking-wider font-body bg-[#f59e0b] text-black flex items-center gap-1">
                    <Star size={10} fill="currentColor" /> Homepage
                  </div>
                )}
                {/* Photo count */}
                {post.photos?.length > 1 && (
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-[10px] font-mono">
                    {post.photos.length} photos
                  </div>
                )}
              </div>
              
              {/* Content */}
              <div className="p-4">
                <h3 className="font-heading text-lg uppercase tracking-wide mb-1" style={{ color: 'var(--dash-text)' }}>
                  {post.title}
                </h3>
                <p className="text-sm font-body mb-2" style={{ color: 'var(--dash-text-muted)' }}>
                  {post.vehicle} • {post.service}
                </p>
                
                {/* Tags */}
                {post.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {post.tags.slice(0, 4).map(tag => (
                      <span key={tag} className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#3b82f6]/10 text-[#3b82f6] border border-[#3b82f6]/20">
                        {tag}
                      </span>
                    ))}
                    {post.tags.length > 4 && (
                      <span className="px-2 py-0.5 text-[10px] text-zinc-500">+{post.tags.length - 4}</span>
                    )}
                  </div>
                )}
                
                {/* Stats */}
                <div className="flex items-center gap-4 text-xs mb-3" style={{ color: 'var(--dash-text-dim)' }}>
                  <span className="flex items-center gap-1">
                    <Eye size={12} /> {post.views || 0} views
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar size={12} /> {new Date(post.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
                
                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {/* Featured Button */}
                  {post.published && !post.featured && (
                    <button
                      onClick={() => setAsFeatured(post)}
                      className="flex items-center justify-center gap-1 px-3 py-2 text-xs uppercase font-body border border-[#f59e0b]/30 text-[#f59e0b] hover:bg-[#f59e0b]/10 bg-transparent transition-colors cursor-pointer"
                      title="Show this car on the homepage"
                      data-testid={`set-featured-${post.id}`}
                    >
                      <Star size={12} /> Feature
                    </button>
                  )}
                  {post.featured && (
                    <div className="flex items-center gap-1 px-3 py-2 text-xs uppercase font-body bg-[#f59e0b]/20 text-[#f59e0b] border border-[#f59e0b]/30">
                      <Star size={12} fill="currentColor" /> On Homepage
                    </div>
                  )}
                  <button
                    onClick={() => togglePublish(post)}
                    className={`flex items-center justify-center gap-1 px-3 py-2 text-xs uppercase font-body border transition-colors cursor-pointer ${
                      post.published
                        ? 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10 bg-transparent'
                        : 'border-[#22c55e]/30 text-[#22c55e] hover:bg-[#22c55e]/10 bg-transparent'
                    }`}
                  >
                    {post.published ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}
                  </button>
                  <button
                    onClick={() => openEditor(post)}
                    className="px-3 py-2 text-xs uppercase font-body border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-colors cursor-pointer bg-transparent"
                  >
                    <Edit2 size={12} />
                  </button>
                  <button
                    onClick={() => deletePost(post)}
                    disabled={deleting === post.id}
                    className="px-3 py-2 text-xs uppercase font-body border border-red-500/20 text-red-500/60 hover:text-red-400 hover:border-red-500/40 transition-colors cursor-pointer bg-transparent disabled:opacity-50"
                  >
                    {deleting === post.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      <AnimatePresence>
        {showEditor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto p-4"
            onClick={closeEditor}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="w-full max-w-3xl my-8 border"
              style={{ backgroundColor: 'var(--dash-card)', borderColor: 'var(--dash-border)' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--dash-border)' }}>
                <h3 className="font-heading text-lg uppercase tracking-wider" style={{ color: 'var(--dash-text)' }}>
                  {editingPost ? 'Edit Post' : 'New Blog Post'}
                </h3>
                <button onClick={closeEditor} className="text-zinc-500 hover:text-white bg-transparent border-none cursor-pointer">
                  <X size={20} />
                </button>
              </div>

              {/* Form */}
              <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Title */}
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--dash-text-muted)' }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Midnight Purple R34 GTR Full Respray"
                    className="w-full border text-sm p-3 focus:outline-none focus:border-[#3b82f6]/50"
                    style={{ backgroundColor: 'var(--dash-bg)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                    data-testid="blog-title-input"
                  />
                </div>

                {/* Vehicle & Service */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--dash-text-muted)' }}>
                      Vehicle *
                    </label>
                    <input
                      type="text"
                      value={form.vehicle}
                      onChange={e => setForm(f => ({ ...f, vehicle: e.target.value }))}
                      placeholder="e.g. Nissan Skyline R34 GTR"
                      className="w-full border text-sm p-3 focus:outline-none focus:border-[#3b82f6]/50"
                      style={{ backgroundColor: 'var(--dash-bg)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                      data-testid="blog-vehicle-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--dash-text-muted)' }}>
                      Service Type
                    </label>
                    <select
                      value={form.service}
                      onChange={e => setForm(f => ({ ...f, service: e.target.value }))}
                      className="w-full border text-sm p-3 focus:outline-none focus:border-[#3b82f6]/50"
                      style={{ backgroundColor: 'var(--dash-bg)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                    >
                      {SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {/* Description with AI Helper */}
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--dash-text-muted)' }}>
                    Description
                  </label>
                  
                  {/* AI Notes Input */}
                  <div className="mb-3 p-3 border border-dashed rounded-sm" style={{ borderColor: 'var(--dash-border)', backgroundColor: 'var(--dash-bg)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-wider flex items-center gap-2" style={{ color: 'var(--dash-text-dim)' }}>
                        <Sparkles size={12} className="text-[#f59e0b]" />
                        AI Helper - Add notes or use voice
                      </span>
                      <div className="flex gap-2">
                        {isRecording ? (
                          <button
                            type="button"
                            onClick={stopRecording}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-xs uppercase tracking-wider font-body cursor-pointer border-none animate-pulse"
                            data-testid="stop-recording-btn"
                          >
                            <StopCircle size={12} /> Stop
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={startRecording}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-xs uppercase tracking-wider font-body cursor-pointer border-none transition-colors"
                            data-testid="start-recording-btn"
                          >
                            <Mic size={12} /> Voice
                          </button>
                        )}
                      </div>
                    </div>
                    <textarea
                      value={aiNotes}
                      onChange={e => setAiNotes(e.target.value)}
                      placeholder="Type quick notes or click Voice to speak... e.g. 'R34 came in faded, stripped to bare metal, fixed rust in quarters, midnight purple respray, 3 stage clear coat'"
                      rows={2}
                      className="w-full border-0 bg-transparent text-sm p-0 resize-none focus:outline-none font-body"
                      style={{ color: 'var(--dash-text)' }}
                      data-testid="ai-notes-input"
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        type="button"
                        onClick={generateDescription}
                        disabled={generatingAI || !aiNotes.trim()}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#f59e0b] to-[#d97706] text-black text-xs uppercase tracking-wider font-heading cursor-pointer border-none disabled:opacity-50 disabled:cursor-not-allowed hover:from-[#fbbf24] hover:to-[#f59e0b] transition-all"
                        data-testid="generate-description-btn"
                      >
                        {generatingAI ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles size={14} />
                            Generate Description
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Final Description */}
                  <textarea
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Your polished description will appear here... (or write your own)"
                    rows={4}
                    className="w-full border text-sm p-3 resize-none focus:outline-none focus:border-[#3b82f6]/50"
                    style={{ backgroundColor: 'var(--dash-bg)', borderColor: 'var(--dash-border)', color: 'var(--dash-text)' }}
                    data-testid="blog-description-input"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--dash-text-muted)' }}>
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {TAG_OPTIONS.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-wider font-body border cursor-pointer transition-colors ${
                          form.tags.includes(tag)
                            ? 'bg-[#3b82f6] text-white border-[#3b82f6]'
                            : 'bg-transparent text-zinc-500 border-white/10 hover:border-white/30'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Photos */}
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--dash-text-muted)' }}>
                    Photos ({form.photos.length}/20)
                  </label>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {form.photos.map((photo, i) => (
                      <div key={i} className="relative aspect-square group">
                        <img src={photo} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removePhoto(i)}
                          className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer border-none"
                        >
                          <X size={12} />
                        </button>
                        {i === 0 && (
                          <span className="absolute bottom-1 left-1 px-1 py-0.5 bg-black/70 text-[8px] text-white uppercase">Cover</span>
                        )}
                      </div>
                    ))}
                    {form.photos.length < 20 && (
                      <label className="aspect-square border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:border-[#3b82f6]/50 transition-colors" style={{ borderColor: 'var(--dash-border)' }}>
                        {uploadingPhotos ? (
                          <Loader2 size={20} className="animate-spin text-[#3b82f6]" />
                        ) : (
                          <>
                            <Upload size={20} style={{ color: 'var(--dash-text-dim)' }} />
                            <span className="text-[10px] mt-1" style={{ color: 'var(--dash-text-dim)' }}>Add</span>
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={e => handlePhotoUpload(e.target.files)}
                          disabled={uploadingPhotos}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Publish Toggle */}
                <div className="flex items-center gap-3 p-3 border" style={{ borderColor: 'var(--dash-border)', backgroundColor: 'var(--dash-bg)' }}>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, published: !f.published }))}
                    className={`w-12 h-6 rounded-full relative transition-colors cursor-pointer border-none ${
                      form.published ? 'bg-[#22c55e]' : 'bg-zinc-700'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                      form.published ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                  <div>
                    <span className="font-body text-sm" style={{ color: 'var(--dash-text)' }}>
                      {form.published ? 'Published' : 'Draft'}
                    </span>
                    <p className="text-xs" style={{ color: 'var(--dash-text-dim)' }}>
                      {form.published ? 'Visible on public blog page' : 'Only visible in admin'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 p-4 border-t" style={{ borderColor: 'var(--dash-border)' }}>
                <button
                  onClick={savePost}
                  disabled={saving}
                  className="flex-1 py-3 bg-[#3b82f6] text-white font-heading text-sm tracking-wider uppercase hover:bg-[#3b82f6]/80 transition-colors cursor-pointer border-none disabled:opacity-50"
                  data-testid="blog-save-btn"
                >
                  {saving ? <Loader2 size={16} className="animate-spin mx-auto" /> : (editingPost ? 'Save Changes' : 'Create Post')}
                </button>
                <button
                  onClick={closeEditor}
                  className="px-6 py-3 border font-heading text-sm tracking-wider uppercase transition-colors cursor-pointer"
                  style={{ borderColor: 'var(--dash-border)', color: 'var(--dash-text-muted)', backgroundColor: 'transparent' }}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export { BlogManager };
